/**
 * Добавление работы в этап плана агроцикла (менеджер): кнопка «+» у этапа
 * открывает этот ящик. Вид работы — из справочника, окно — датами. Работа
 * становится обычной работой плана (`origin='added'`): её можно запустить
 * кампанией так же, как работу из шаблона.
 */

import { useMemo, useState } from 'react';
import { DateRangePicker, Select, TextField, type Choice, type DateRange } from '../../ui/forms';
import { DetailDrawer, DrawerFormActions } from '../../ui/DetailDrawer';
import { LoadError, failed } from '../../ui/LoadError';
import { Skeleton } from '../../ui/Skeleton';
import { COMMON } from '../../../i18n/common';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { api, ApiError } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { agrocycleApi, type AgrocycleDetail, type PlanStage } from './agrocycleApi';
import { RequiredToggle } from './PlanFields';

const m = defineMessages({
  title: { id: 'cycle.plan.add.title', defaultMessage: 'Новая работа: {stage}', description: 'Заголовок ящика добавления работы в этап плана агроцикла (менеджер); {stage} — название этапа' },
  kindField: { id: 'cycle.plan.add.kind', defaultMessage: 'Вид работы', description: 'Подпись поля выбора вида работы из справочника при добавлении работы в этап (менеджер)' },
  labelField: { id: 'cycle.plan.add.labelField', defaultMessage: 'Подпись', description: 'Подпись поля: свободная подпись добавляемой работы плана; поле необязательное — пометку «(необязательно)» ставит сам компонент формы (менеджер)' },
  windowField: { id: 'cycle.plan.add.window', defaultMessage: 'Окно работы', description: 'Подпись поля периода: окно добавляемой работы плана (менеджер); одно поле «с — по»' },
  needKind: { id: 'cycle.plan.add.needKind', defaultMessage: 'Выберите вид работы.', description: 'Причина выключенной кнопки «Добавить работу» в ящике: не выбран вид работы' },
  needWindow: { id: 'cycle.plan.add.needWindow', defaultMessage: 'Укажите окно работы.', description: 'Причина выключенной кнопки «Добавить работу» в ящике: не выбрано окно' },
  submit: { id: 'cycle.plan.add.submit', defaultMessage: 'Добавить работу', description: 'Кнопка в ящике добавления работы в этап плана (менеджер)' },
  catalogWhat: { id: 'cycle.plan.add.catalogWhat', defaultMessage: 'справочник видов работ', description: 'Что не загрузилось (винительный падеж, в «Не удалось загрузить …»): справочник видов работ' },
  failed: { id: 'cycle.plan.add.failed', defaultMessage: 'Не получилось — повторите.', description: 'Ошибка в ящике добавления работы (менеджер), когда сервер не дал своего текста' },
  reason: { id: 'cycle.plan.reason.added', defaultMessage: 'добавлена менеджером', description: 'Причина правки плана, которую приложение отправляет серверу; потом видна в журнале плана' },
  added: { id: 'cycle.plan.add.done', defaultMessage: 'Работа добавлена в план: {name}', description: 'Уведомление после добавления работы в этап плана; {name} — название работы' },
});

type Params = Record<string, string | number | boolean | null | undefined>;

export function AddWorkDrawer({
  cycle,
  stage,
  params,
  onClose,
  onAdded,
}: {
  cycle: AgrocycleDetail;
  stage: PlanStage;
  params: Params;
  onClose: () => void;
  onAdded: (cycle: AgrocycleDetail, message: string) => void;
}) {
  const t = useT();
  const catalog = useRequest(() => api.agrocycleCatalog(params as never), [JSON.stringify(params)]);
  const items = useMemo<Choice[]>(
    () => (((catalog.data as unknown as { items?: { id: number; name: string }[] })?.items) ?? []).map((item) => ({ id: String(item.id), name: item.name })),
    [catalog.data],
  );
  const [kind, setKind] = useState('');
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(true);
  // Окно по умолчанию — окно этапа; если конца нет, работа однодневная (конец = начало).
  const startFrom = stage.window_from ?? cycle.plan_from ?? null;
  const [range, setRange] = useState<DateRange>({ from: startFrom, to: stage.window_to ?? startFrom });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const min = `${cycle.anchor_year - 2}-01-01`;
  const max = `${cycle.anchor_year + 2}-12-31`;
  const kindName = items.find((item) => item.id === kind)?.name ?? '';
  const ready = !!kind && !!range.from;
  // Несохранённый ввод: вид, подпись, обязательность или окно изменены против открытия.
  const dirty = !!kind || label.trim() !== '' || !required || range.from !== startFrom || range.to !== (stage.window_to ?? startFrom);

  const submit = async () => {
    if (busy || !ready || !range.from) return;
    setBusy(true);
    setError(null);
    try {
      const result = await agrocycleApi.addWork(
        cycle.key,
        { stage_id: stage.id, catalog_work_id: Number(kind), label: label.trim(), is_required: required, approximate: false, window_from: range.from, window_to: range.to && range.to !== range.from ? range.to : null, reason: t(m.reason) },
        params,
      );
      onAdded(result.cycle, t(m.added, { name: label.trim() || kindName }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t(m.failed));
      setBusy(false);
    }
  };

  return (
    <DetailDrawer title={t(m.title, { stage: stage.name })} onClose={onClose} isDirty={dirty}>
      <div className="agc-drawer">
        {failed(catalog) ? (
          <LoadError compact what={t(m.catalogWhat)} error={catalog.error} onRetry={catalog.reload} />
        ) : !catalog.data ? (
          <Skeleton variant="block" height={240} />
        ) : (
          <div className="agc-form">
            <Select label={t(m.kindField)} value={kind} options={items} onChange={setKind} searchable />
            <TextField label={t(m.labelField)} isRequired={false} value={label} onChange={setLabel} />
            <RequiredToggle value={required} onChange={setRequired} />
            <DateRangePicker label={t(m.windowField)} value={range} onChange={setRange} minValue={min} maxValue={max} />
            {error ? (
              <p className="agc-error" role="alert">
                {error}
              </p>
            ) : null}
            <DrawerFormActions
              note={!kind ? t(m.needKind) : !range.from ? t(m.needWindow) : undefined}
              cancel={t(COMMON.cancel)}
              primary={{ label: t(m.submit), isDisabled: !ready, isPending: busy, onPress: () => void submit() }}
            />
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}
